import { NavLink, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Loader2, Menu } from "lucide-react";
import { Sheet, SheetClose, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { AccountMenu } from "@/components/AccountMenu";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { StudioShell } from "@/components/StudioShell";
import { useStudioTheme } from "@/lib/theme";

const links = [
  ["/dashboard", "Dashboard"],
  ["/volunteers", "Volunteers"],
  ["/teams", "Teams"],
  ["/gifts", "Gifts"],
  ["/settings", "Settings"],
  ["/billing", "Billing"],
];

export const AppShell = ({ requireChurch = true }) => {
  const { user, church, loading, logout, isAdmin } = useAuth();
  const locked = church?.billing?.status === "expired";
  const base = locked ? links.filter(([to]) => to === "/billing") : links;
  const navLinks = isAdmin ? [...base, ["/admin/codes", "Codes"]] : base;
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const studio = !!church && church.dashboard_theme?.effective === "studio";
  useStudioTheme(studio);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-teal" /></div>
  );
  if (!user) return <Navigate to="/" replace />;
  if (requireChurch && !church) return <Navigate to="/onboarding" replace />;
  if (church?.billing?.status === "expired" && !["/billing", "/admin/codes"].includes(pathname)) return <Navigate to="/billing" replace />;

  const signOut = async () => { await logout(); navigate("/"); };
  const banner = church?.billing && church.billing.status !== "active" && (
    <div className={`${church.billing.status === "expired" ? "bg-red-50 text-red-900" : "bg-teal-soft text-navy"} text-sm`} data-testid="trial-banner">
      <div className="max-w-6xl mx-auto px-6 py-2.5 flex flex-wrap items-center justify-between gap-2">
        <span>{church.billing.status === "expired" ? "Choose a plan or enter a discount code to unlock your dashboard." : `${church.billing.days_left} days left in your free trial.`}</span>
        <NavLink to="/billing" className="font-medium underline underline-offset-4" data-testid="trial-banner-link">Choose a plan</NavLink>
      </div>
    </div>
  );
  if (studio) return <StudioShell church={church} user={user} links={navLinks} onSignOut={signOut} banner={banner}><Outlet /></StudioShell>;

  return (
    <div className="min-h-screen bg-paper">
      <header className="bg-white border-b border-line sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center gap-10">
          <Logo to="/" />
          {church && (
            <nav className="hidden md:flex gap-7 text-[15px]">
              {navLinks.map(([to, label]) => (
                <NavLink key={to} to={to} data-testid={`nav-${label.toLowerCase()}`}
                  className={({ isActive }) => `transition-colors ${isActive ? "text-teal font-medium" : "text-navy hover:text-teal"}`}>
                  {label}
                </NavLink>
              ))}
            </nav>
          )}
          <div className="ml-auto flex items-center gap-5 text-[15px]">
            {!church && <AccountMenu />}
            {church && (
              <Sheet>
                <SheetTrigger className="md:hidden text-navy" data-testid="mobile-menu-btn" aria-label="Open menu"><Menu size={22} /></SheetTrigger>
                <SheetContent side="left" className="bg-white">
                  <nav className="flex flex-col gap-5 mt-10 text-lg">
                    {navLinks.map(([to, label]) => (
                      <SheetClose asChild key={to}>
                        <NavLink to={to} data-testid={`mobile-nav-${label.toLowerCase()}`}
                          className={({ isActive }) => (isActive ? "text-teal font-medium" : "text-navy")}>{label}</NavLink>
                      </SheetClose>
                    ))}
                  </nav>
                </SheetContent>
              </Sheet>
            )}
            <span className="text-muted-ink hidden sm:block" data-testid="nav-church-name">{church?.name || user.email}</span>
            <button data-testid="sign-out-btn" className="text-navy hover:text-teal transition-colors"
              onClick={signOut}>Sign out</button>
          </div>
        </div>
      </header>
      {banner}
      <main className="max-w-6xl mx-auto px-6 py-10"><Outlet /></main>
    </div>
  );
};
